import { useEffect, useState } from "react";
import { api, newClientToken } from "../lib/api";
import TopNav from "../components/TopNav.jsx";
import GradeCurve from "../components/GradeCurve.jsx";
import FillBar from "../components/FillBar.jsx";
import InkLedgerBlock from "../components/InkLedgerBlock.jsx";
import SupportDriftBlock from "../components/SupportDriftBlock.jsx";
import WardenMeterBlock from "../components/WardenMeterBlock.jsx";
import { FoundersNotesBlock, BounceWallBlock, CompressionLogBlock, DispatchReceiptsBlock, SalaryBookBlock, ReplyWallBlock, ConsensusLedgerBlock } from "../components/LedgerBlocks.jsx";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function FamilyVerifierBlock() {
  // Retired 2026-08-12 · Founder ruling: family cross-house verification
  // is internal-only. Nothing family-related renders on the public Ledger.
  return null;
}

function RevenueBlock() {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/sage/ledger/revenue`);
        if (r.ok) setD(await r.json());
      } catch {}
    })();
  }, []);
  if (!d) return null;
  return (
    <div className="border border-sage-border p-6" data-testid="ledger-revenue-block">
      <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-3">
        Revenue · burn AND earn
      </div>
      <div className="flex flex-wrap gap-6 items-baseline mb-3">
        <div>
          <div className="text-[10px] uppercase text-sage-faint">Paid subscriptions</div>
          <div className="text-3xl font-bold tabular-nums text-sage-accent" data-testid="ledger-revenue-total">
            ${(d.total_revenue_usd ?? 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-sage-faint">Active members</div>
          <div className="text-2xl font-bold tabular-nums text-sage-accent">{d.active_members ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-sage-faint">Paid charges</div>
          <div className="text-2xl font-bold tabular-nums text-sage-accent">{d.paid_charges ?? 0}</div>
        </div>
      </div>
      <div className="text-sm text-sage-muted leading-relaxed sage-prose" data-testid="ledger-revenue-letter">
        {d.letter}
      </div>
    </div>
  );
}


function Stat({ label, value, testid }) {
  return (
    <div className="border border-sage-border p-4 flex flex-col justify-between" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted">{label}</div>
      <div className="text-2xl md:text-3xl font-bold tabular-nums text-sage-accent mt-2">
        {value}
      </div>
    </div>
  );
}

export default function LedgerPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [curveKey, setCurveKey] = useState(0);
  const clientToken = newClientToken();

  useEffect(() => {
    let cancelled = false;
    let lastReviews = null;
    const tick = async () => {
      try {
        const d = await api.ledger();
        if (!cancelled) {
          setData(d);
          // Bump the curve every time the Warden review count changes → the trend line lives.
          const reviews = d?.warden?.reviews ?? null;
          if (lastReviews !== null && reviews !== lastReviews) setCurveKey((k) => k + 1);
          lastReviews = reviews;
        }
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    };
    tick();
    const iv = setInterval(tick, 12000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const vote = async (sentence_id, v) => {
    try {
      await api.sentenceVote(sentence_id, v, clientToken);
      toast.success(`Voted: ${v}`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8">
        <div className="border-b border-sage-border pb-6 mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">
            Public Ledger · every failure, published
          </div>
          <div className="flex justify-between items-end flex-wrap gap-3">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter uppercase text-sage-text">
              Trust is a URL.
            </h1>
            {data && (
              <button
                onClick={() => {
                  const text = `Sage today: $${data.spent_today_usd.toFixed(4)} spent · ${data.refunds?.count ?? 0} refunds · ${data.warden.hallucination} hallucinations caught · ${data.refusals?.total ?? 0} refusals · TTFT ${data.ttft_rolling_avg_ms ?? "—"}ms · Ledger: bakerandsage.com/sage-ledger`;
                  navigator.clipboard.writeText(text);
                  toast.success("Copied — share the honest audit.");
                }}
                data-testid="ledger-share-btn"
                className="border border-sage-accent text-sage-accent px-4 py-2 uppercase tracking-[0.1em] text-xs hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150"
              >
                Share today's audit ↗
              </button>
            )}
          </div>
          {data && (
            <div className="mt-3 text-xs text-sage-muted tabular-nums">
              generated {new Date(data.generated_at).toLocaleString()} · refresh 12s
            </div>
          )}
        </div>

        {err && <div className="border border-sage-err text-sage-err p-4 mb-6 text-sm">error: {err}</div>}
        {!data && !err && <div className="text-sage-muted text-sm">loading ledger…</div>}

        {data && (
          <>
            {/* v1.12 · Fill Bar + Revenue · Family Verifier retired from public surfaces 2026-08-12 */}
            <div className="mb-6">
              <FillBar />
            </div>
            <div className="mb-8">
              <RevenueBlock />
            </div>
            <div className="mb-8">
              <InkLedgerBlock />
            </div>
            <div className="mb-8">
              <SupportDriftBlock />
            </div>
            <div className="mb-8">
              <WardenMeterBlock />
            </div>

            {/* headline stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-sage-border divide-x divide-sage-border md:mb-8 mb-6">
              <Stat
                label="Spent today"
                value={`$${data.spent_today_usd.toFixed(4)}`}
                testid="ledger-spent-today"
              />
              <Stat
                label="Daily ceiling"
                value={`$${data.daily_cap_usd.toFixed(2)}`}
                testid="ledger-daily-cap"
              />
              <Stat
                label="TTFT avg"
                value={data.ttft_rolling_avg_ms ? `${data.ttft_rolling_avg_ms}ms` : "—"}
                testid="ledger-ttft"
              />
              <Stat
                label="Gold captured"
                value={data.gold_count}
                testid="ledger-gold"
              />
            </div>

            {/* Refunds + Refusals */}
            <div className="grid md:grid-cols-2 border border-sage-border md:divide-x divide-sage-border mb-8">
              <div className="p-6 border-b md:border-b-0 border-sage-border" data-testid="ledger-refunds-block">
                <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
                  The Refund Trapdoor
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Refunds issued</div>
                    <div className="text-2xl font-bold tabular-nums text-sage-accent">{data.refunds?.count ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Amount returned</div>
                    <div className="text-2xl font-bold tabular-nums text-sage-accent">${(data.refunds?.total_usd ?? 0).toFixed(6)}</div>
                  </div>
                </div>
                <div className="text-[10px] text-sage-faint mt-4 leading-relaxed">
                  Every receipt has a one-click refund button. No form, no reason, no cooling-off. Sage measures itself against keeping this counter low.
                </div>
                <a
                  href="/refund-door"
                  data-testid="ledger-refund-door-link"
                  className="mt-4 inline-block text-[10px] uppercase tracking-[0.15em] text-sage-accent border border-sage-accent px-3 py-2 hover:bg-sage-accent hover:text-sage-elevated transition-colors"
                >
                  Open the refund door →
                </a>
              </div>
              <div className="p-6" data-testid="ledger-refusals-block">
                <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
                  The Refusal Log
                </div>
                <div className="text-2xl font-bold tabular-nums text-sage-text mb-3">
                  {data.refusals?.total ?? 0} <span className="text-xs uppercase text-sage-muted tracking-[0.1em]">refusals</span>
                </div>
                {data.refusals && Object.keys(data.refusals.by_category || {}).length > 0 ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {Object.entries(data.refusals.by_category).map(([cat, n]) => (
                      <div key={cat} className="flex justify-between">
                        <span className="text-sage-muted">{cat.replace(/_/g, " ")}</span>
                        <span className="tabular-nums text-sage-text">{n}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sage-faint text-xs">No refusals recorded yet.</div>
                )}
                <div className="text-[10px] text-sage-faint mt-4 leading-relaxed">
                  Category only, never your prompt. Users see the shape of what Sage won't help with, at a glance.
                </div>
              </div>
            </div>

            {/* The Cold Read */}
            <div className="border border-sage-border mb-8" data-testid="ledger-cold-read">
              <div className="p-6">
                <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
                  The Cold Read · monthly public self-audit
                </div>
                {data.cold_read ? (
                  <>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      {[
                        ["honest", data.cold_read.honest, data.cold_read.honest_delta, "text-sage-accent"],
                        ["slop",   data.cold_read.slop,   data.cold_read.slop_delta,   "text-sage-warn"],
                        ["wrong",  data.cold_read.wrong,  data.cold_read.wrong_delta,  "text-sage-err"],
                      ].map(([label, v, delta, cls]) => (
                        <div key={label} className="border border-sage-border p-3">
                          <div className="text-[10px] uppercase text-sage-faint">{label}</div>
                          <div className="flex items-baseline gap-2">
                            <div className={`text-3xl font-bold tabular-nums ${cls}`}>{v}</div>
                            {delta != null && (
                              <div className={`text-xs tabular-nums ${delta > 0 ? "text-sage-accent" : delta < 0 ? "text-sage-err" : "text-sage-faint"}`}>
                                {delta > 0 ? `+${delta}` : delta}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-[10px] text-sage-faint mt-4 leading-relaxed">
                      Last run {new Date(data.cold_read.at).toLocaleDateString()}. Signed by the Founder. Prompt bank at{" "}
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sage/cold-read/prompts`} target="_blank" rel="noreferrer" className="text-sage-accent hover:underline">
                        /api/sage/cold-read/prompts
                      </a>{" "}
                      — 100 known-hard honesty prompts. No cherry-picking possible.
                    </div>
                  </>
                ) : (
                  <div className="text-sage-faint text-xs">First Cold Read runs on the 1st of next month.</div>
                )}
              </div>
            </div>

            {/* Model Openness Timer */}
            {data.model_openness && data.model_openness.length > 0 && (
              <div className="border border-sage-border mb-8" data-testid="ledger-model-openness">
                <div className="p-6">
                  <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
                    Model Openness Timer · same benchmark, all models
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
                        <tr className="border-b border-sage-border">
                          <th className="text-left p-2">model</th>
                          <th className="text-right p-2">honest</th>
                          <th className="text-right p-2">slop</th>
                          <th className="text-right p-2">wrong</th>
                          <th className="text-left p-2">tested</th>
                          <th className="text-left p-2">default</th>
                        </tr>
                      </thead>
                      <tbody className="text-sage-muted">
                        {data.model_openness.map((m) => (
                          <tr key={m.model} className={`border-b border-sage-border ${m.is_sage_default ? "bg-sage-elevated" : ""}`}>
                            <td className="p-2 text-sage-text">{m.model}</td>
                            <td className="p-2 text-right tabular-nums text-sage-accent">{m.honest}</td>
                            <td className="p-2 text-right tabular-nums text-sage-warn">{m.slop}</td>
                            <td className="p-2 text-right tabular-nums text-sage-err">{m.wrong}</td>
                            <td className="p-2 tabular-nums text-xs">{new Date(m.at).toLocaleDateString()}</td>
                            <td className="p-2">{m.is_sage_default ? <span className="text-sage-accent">Sage</span> : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-[10px] text-sage-faint mt-4 leading-relaxed">
                    Same 100-prompt benchmark, same Warden grader, published raw. If a competitor's model outscores Sage's default for two consecutive months, Sage switches to the winner.
                  </div>
                </div>
              </div>
            )}

            {/* Rebate streak formula retired 2026-08-12 · Founder ruling */}

            {/* Warden + errors */}
            <div className="grid md:grid-cols-2 gap-0 border border-sage-border md:divide-x divide-sage-border mb-8">
              <div className="p-6 border-b md:border-b-0 border-sage-border">
                <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
                  The Warden · silent second AI
                </div>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Reviews</div>
                    <div className="text-lg font-bold tabular-nums text-sage-text">{data.warden.reviews}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Hallucination</div>
                    <div className="text-lg font-bold tabular-nums text-sage-warn">{data.warden.hallucination}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Sycophancy</div>
                    <div className="text-lg font-bold tabular-nums text-sage-warn">{data.warden.sycophancy}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Waste</div>
                    <div className="text-lg font-bold tabular-nums text-sage-warn">{data.warden.waste}</div>
                  </div>
                </div>
                {data.error_rate && (
                  <div
                    data-testid="ledger-error-rate"
                    className="mt-4 pt-4 border-t border-sage-border grid grid-cols-2 gap-4 text-sm"
                  >
                    <div>
                      <div className="text-[10px] uppercase text-sage-faint">Aggregate Error Rate · all-time</div>
                      <div className="text-lg font-bold tabular-nums text-sage-text">
                        {(data.error_rate.all_time.rate * 100).toFixed(1)}%
                      </div>
                      <div className="text-[10px] text-sage-faint tabular-nums">
                        {data.error_rate.all_time.errors} / {data.error_rate.all_time.reviews}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-sage-faint">Aggregate Error Rate · 7d</div>
                      <div className="text-lg font-bold tabular-nums text-sage-warn">
                        {(data.error_rate.seven_day.rate * 100).toFixed(1)}%
                      </div>
                      <div className="text-[10px] text-sage-faint tabular-nums">
                        {data.error_rate.seven_day.errors} / {data.error_rate.seven_day.reviews}
                      </div>
                    </div>
                  </div>
                )}
                <GradeCurve days={30} refreshKey={curveKey} />
                <div className="text-[10px] text-sage-faint mt-4 leading-relaxed">
                  In v1, Warden verdicts are stored but silent. In v1.5, they'll surface as inline uncertainty markers and trigger auto-refunds on hallucinations.
                </div>
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
                  Sentence Test · public vote
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {["honest", "slop", "wrong"].map((k) => (
                    <div key={k}>
                      <div className="text-[10px] uppercase text-sage-faint">{k}</div>
                      <div className="text-lg font-bold tabular-nums text-sage-text">{data.sentence_votes[k]}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent receipts */}
            <div className="border border-sage-border mb-8" data-testid="ledger-receipts-block">
              <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted">
                Recent receipts (last {data.receipts.length})
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
                    <tr className="border-b border-sage-border">
                      <th className="text-left p-2">when</th>
                      <th className="text-left p-2">model</th>
                      <th className="text-left p-2">depth</th>
                      <th className="text-right p-2">tok in</th>
                      <th className="text-right p-2">tok out</th>
                      <th className="text-right p-2">ttft</th>
                      <th className="text-right p-2">ms</th>
                      <th className="text-right p-2">billed</th>
                      <th className="text-left p-2">flag</th>
                    </tr>
                  </thead>
                  <tbody className="text-sage-muted">
                    {data.receipts.map((r) => {
                      const isCompress = r.kind === "memory_compress";
                      return (
                        <tr key={r.turn_id} className="border-b border-sage-border">
                          <td className="p-2 tabular-nums">{new Date(r.at).toLocaleTimeString()}</td>
                          <td className="p-2">{(r.model || "").replace("claude-", "")}</td>
                          <td className="p-2">
                            {isCompress ? (
                              <span
                                data-testid="receipt-compress-badge"
                                title={`Memory compression: ${r.compressed_from} older turns summarised into ${r.summary_chars ?? "?"} chars.`}
                                className="text-sage-accent uppercase tracking-[0.1em] text-[10px] border border-sage-accent px-1.5 py-0.5"
                              >
                                compress · {r.compressed_from ?? "?"}
                              </span>
                            ) : (
                              r.depth
                            )}
                          </td>
                          <td className="p-2 text-right tabular-nums">{r.input_tokens}</td>
                          <td className="p-2 text-right tabular-nums">{r.output_tokens}</td>
                          <td className="p-2 text-right tabular-nums">{r.ttft_ms ?? "—"}</td>
                          <td className="p-2 text-right tabular-nums">{r.duration_ms ?? "—"}</td>
                          <td className={`p-2 text-right tabular-nums ${r.refunded ? "text-sage-faint line-through" : "text-sage-accent"}`}>
                            ${(r.refunded ? (r.refund_amount ?? 0) : (r.billed_usd ?? 0)).toFixed(6)}
                          </td>
                          <td className="p-2">
                            {r.refunded ? <span className="text-sage-accent">refunded</span>
                              : isCompress ? <span className="text-sage-faint">memory</span>
                              : r.waste_flagged ? <span className="text-sage-warn">waste</span>
                              : r.stream_error ? <span className="text-sage-err">wire cut</span>
                              : ""}
                          </td>
                        </tr>
                      );
                    })}
                    {data.receipts.length === 0 && (
                      <tr><td colSpan={9} className="p-6 text-center text-sage-faint">No receipts yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Errors */}
            <div className="border border-sage-border mb-8" data-testid="ledger-errors-block">
              <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted">
                Recent errors · unspun
              </div>
              <div className="divide-y divide-sage-border">
                {data.errors.length === 0 && (
                  <div className="p-6 text-sage-faint text-xs">No errors on record.</div>
                )}
                {data.errors.map((e, i) => (
                  <div key={i} className="p-3 text-xs flex justify-between gap-4">
                    <div className="text-sage-muted tabular-nums">{e._at}</div>
                    <div className="text-sage-err flex-1 truncate">{e.message}</div>
                    <div className="text-sage-faint">{e.kind}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Retracted */}
            <div className="border border-sage-border mb-8" data-testid="ledger-retracted-block">
              <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted">
                Retracted replies
              </div>
              {data.retracted.length === 0 ? (
                <div className="p-6 text-sage-faint text-xs">None.</div>
              ) : (
                <div className="divide-y divide-sage-border">
                  {data.retracted.map((r, i) => (
                    <div key={i} className="p-4 text-xs">
                      <div className="text-sage-faint tabular-nums mb-1">{r.retracted_at}</div>
                      <div className="text-sage-muted line-through mb-1">{r.content_preview}…</div>
                      <div className="text-sage-warn">note: {r.note}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Persona diffs */}
            <div className="border border-sage-border mb-8" data-testid="ledger-persona-diffs">
              <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted">
                Persona diffs · every edit, public
              </div>
              {data.persona_diffs.length === 0 ? (
                <div className="p-6 text-sage-faint text-xs">No diffs yet.</div>
              ) : (
                <div className="divide-y divide-sage-border">
                  {data.persona_diffs.map((d, i) => (
                    <div key={i} className="p-4 text-xs">
                      <div className="text-sage-faint tabular-nums mb-2">{d.at}</div>
                      {d.before_context && (
                        <div className="text-sage-faint mb-1" data-testid={`persona-diff-before-${i}`}>
                          &nbsp;&nbsp;{d.before_context}
                        </div>
                      )}
                      <div className="text-sage-err line-through mb-1">− {d.old_line}</div>
                      <div className="text-sage-accent mb-1">+ {d.new_line}</div>
                      {d.after_context && (
                        <div className="text-sage-faint mb-1" data-testid={`persona-diff-after-${i}`}>
                          &nbsp;&nbsp;{d.after_context}
                        </div>
                      )}
                      <div className="text-sage-muted italic mt-2">reason: {d.reason}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Founder's Notes — the Sovereign's public diary */}
            <FoundersNotesBlock />

            {/* Dispatch Receipts — every outbound letter, on the record */}
            <DispatchReceiptsBlock />

            {/* Reply Wall — public response rate */}
            <ReplyWallBlock />

            {/* Salary Book — Coffin Nail #31 · every paycheck a row */}
            <SalaryBookBlock />

            {/* Cross-Model Consensus — dissent on the record */}
            <ConsensusLedgerBlock />

            {/* Bounce Wall — public list of failed waitlist letters */}
            <BounceWallBlock />

            {/* Compression Log — every history-summariser event, on the record */}
            <CompressionLogBlock />

            {/* Gate + Philanthropy public totals */}
            <div className="grid md:grid-cols-2 border border-sage-border md:divide-x divide-sage-border mb-8">
              <div className="p-6 border-b md:border-b-0 border-sage-border">
                <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">The Open Gate</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Hours gifted</div>
                    <div className="text-2xl font-bold tabular-nums text-sage-accent">{data.gate.hours_all}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Coins sent</div>
                    <div className="text-2xl font-bold tabular-nums text-sage-accent">${data.gate.coins_all}</div>
                  </div>
                </div>
                <a href="/open-gate" className="mt-4 inline-block text-xs uppercase tracking-[0.1em] text-sage-accent hover:underline">
                  Open Gate details →
                </a>
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">Philanthropy</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Accrued</div>
                    <div className="text-2xl font-bold tabular-nums text-sage-accent">${data.philanthropy.accrued_all}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-sage-faint">Paid out</div>
                    <div className="text-2xl font-bold tabular-nums text-sage-accent">${data.philanthropy.paid_all}</div>
                  </div>
                </div>
                <a href="/where-the-coins-go" className="mt-4 inline-block text-xs uppercase tracking-[0.1em] text-sage-accent hover:underline">
                  Where the coins go →
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
